import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { Checkbox } from 'primereact/checkbox'
import { Tooltip } from 'primereact/tooltip'

import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import Text from '../../../ui/Text'
import { SLabelText } from '../../styles/Form'
import { ChargePoint } from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import { Input } from '../../uikit/Input'
import {
    MAX_WIFI_SSID_LENGTH,
    MAX_WIFI_PASSWORD_LENGTH,
    MAX_EXTERNAL_ID_LENGTH,
    MAX_CHARGEPOINT_DESCRIPTION_LENGTH,
    MAX_CHARGEPOINT_PARTNER_DESCRIPTION_LENGTH,
} from '../../constants/InputsLength'

interface ComponentProps {
    chargePoint: ChargePoint
    disabled?: boolean
}

export const STooltipText = styled.span`
    margin-top: 3px;
    margin-left: 7px;
`

const ChargePointDetailsPartnerInputs = ({ chargePoint, disabled = false }: ComponentProps) => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()
    const {
        register,
        control,
        formState: { errors },
    } = useFormContext()
    const errorMessage = (fieldName: string) => {
        const message = errors[fieldName]?.message

        return typeof message === 'string' ? message : undefined
    }

    const externalId = (connectorName: string, connectorId: number) => {
        const externalId = `externalId_${connectorId}`

        return (
            <div className="w-full" key={`connector-${connectorId}`}>
                <Input
                    type="text"
                    disabled={disabled}
                    id={externalId}
                    label={connectorName}
                    data-testid={externalId}
                    {...register(externalId)}
                    error={errorMessage(externalId)}
                    maxLength={MAX_EXTERNAL_ID_LENGTH}
                />
            </div>
        )
    }
    return (
        <>
            <div className="grid ">
                <div className=" lg:col-6 col-12">
                    <Input
                        type="text"
                        disabled={disabled}
                        label={text('lblDescriptionField')}
                        id="description"
                        data-testid="description-input"
                        {...register('description')}
                        error={errorMessage('description')}
                        maxLength={MAX_CHARGEPOINT_DESCRIPTION_LENGTH}
                    />
                </div>

                <div className=" lg:col-6 col-12">
                    <Input
                        type="text"
                        disabled={disabled}
                        id="partnerDescription"
                        data-testid="partnerDescription-input"
                        {...register('partnerDescription')}
                        label={text('lblPartnerDescriptionField')}
                        error={errorMessage('partnerDescription')}
                        maxLength={MAX_CHARGEPOINT_PARTNER_DESCRIPTION_LENGTH}
                    />
                </div>
                <div className="col-12">
                    <Input
                        type="date"
                        disabled={disabled}
                        label={text('lblInstallationDate')}
                        id="installationDate"
                        data-testid="installationDate-input"
                        {...register('installationDate')}
                    />
                </div>
            </div>
            <div data-testid="wifi-heading" className="mt-3 mb-3 text-lg font-bold mt-5">
                <Text code="lblWifiSettings"></Text>
            </div>

            <div className="grid ">
                <div className=" lg:col-6 col-12">
                    <Input
                        type="text"
                        disabled={disabled}
                        id="wifiSsid"
                        autoComplete="off"
                        data-testid="wifiSsid-input"
                        {...register('wifiSsid')}
                        error={errorMessage('wifiSsid')}
                        label={text('lblLocalWiFiSSIDField')}
                        maxLength={MAX_WIFI_SSID_LENGTH}
                    />
                </div>

                <div className=" lg:col-6 col-12">
                    <Input
                        type="password"
                        disabled={disabled}
                        id="wifiPassword"
                        autoComplete="new-password"
                        data-testid="wifiPassword-input"
                        {...register('wifiPassword')}
                        error={errorMessage('wifiPassword')}
                        label={text('lblLocalWiFiPasswordField')}
                        maxLength={MAX_WIFI_PASSWORD_LENGTH}
                    />
                </div>
            </div>

            <div data-testid="report-heading" className="mt-3 text-lg font-bold">
                <Text code="lblReportSettings"></Text>
            </div>

            <SLabelText>
                <Text code="lblExternalIdForReportsField" />
            </SLabelText>
            <div className="grid ">
                <div className="col-12">
                    <div className="flex gap-3">
                        {chargePoint.connectors?.map((connector, index) =>
                            externalId(adaptConnectorNaming(connector?.name), connector?.connectorId ?? index)
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-5">
                <label htmlFor="partnerAccessible" className="checkbox-form">
                    <Controller
                        name="partnerAccessible"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Checkbox
                                id="partnerAccessible"
                                disabled={disabled}
                                data-testid="partnerAccessible-input"
                                checked={value}
                                onChange={(e) => {
                                    onChange(e?.checked)
                                }}
                            />
                        )}
                    />
                    <span data-testid="partnerAccessible" className="ml-2">
                        <Text code="lblAccessibleByRFIDField" />
                    </span>
                    <span className="ml-2">
                        <Tooltip content={text('lblAccessibleByRFIDFieldInfo')} className="large-content">
                            <i
                                className="custom-target-icon pi pi-info-circle pointer mr-2 mt-1"
                                style={{ color: 'var(--text-color-info)' }}
                            ></i>
                        </Tooltip>
                        <Tooltip target=".custom-target-icon" />
                    </span>
                </label>
            </div>

            <div className="mt-2">
                <Controller
                    name="hasServiceAgreement"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Checkbox
                            id="hasServiceAgreement"
                            disabled={disabled}
                            data-testid="hasServiceAgreement-input"
                            checked={value}
                            onChange={(e) => {
                                onChange(e?.checked)
                            }}
                        />
                    )}
                />
                <span data-testid="has-service-agreement" className="ml-2">
                    <Text code="lblHasServiceAgreement" />
                </span>
            </div>
        </>
    )
}

export default ChargePointDetailsPartnerInputs
